import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Target,
  Activity
} from 'lucide-react';
import { Button } from '../ui/Button';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { useBots } from '@/contexts/BotsContext';
import { supabase } from '@/lib/supabaseClient';
import { getStrategyLabel } from '../utils/strategies';
import { formatPct, formatCurrency, formatNumber } from '../utils/format';
import { cn } from '../utils/cn';

export const BotStatsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { bots } = useBots();
  const [loading, setLoading] = useState(true);
  const [botStats, setBotStats] = useState<any>(null);
  const [trades, setTrades] = useState<any[]>([]);

  const botId = searchParams.get('botId');
  const bot = bots.find(b => b.id === botId);

  useEffect(() => {
    if (botId) {
      fetchBotStats();
    }
  }, [botId]);

  const fetchBotStats = async () => {
    if (!botId) return;
    
    setLoading(true);
    try {
      // Fetch bot statistics and recent trades
      const { data: tradesData, error: tradesError } = await supabase
        .from('trade_log')
        .select('*')
        .eq('bot_id', botId)
        .order('timestamp', { ascending: false })
        .limit(50);

      if (tradesError) throw tradesError;

      setTrades(tradesData || []);
      
      // Calculate statistics from trades
      const stats = calculateBotStatistics(tradesData || []);
      setBotStats(stats);
      
    } catch (error) {
      console.error('Error fetching bot stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateBotStatistics = (trades: any[], initialCapital?: number) => {
    const capital = initialCapital || bot?.initial_capital || bot?.account_balance || 1000;
    const sellTrades = trades.filter(t => t.type === 'sell');
    const totalTrades = sellTrades.length;

    if (totalTrades === 0) {
      return {
        totalTrades: 0,
        totalProfit: 0,
        totalProfitAmount: 0,
        winRate: 0,
        avgProfit: 0,
        maxProfit: 0,
        minProfit: 0,
        totalFees: 0
      };
    }
    
    const totalProfitAmount = sellTrades.reduce((sum, trade) => sum + (trade.profit_amount || 0), 0);
    const totalFees = sellTrades.reduce((sum, trade) => sum + (trade.fee || 0), 0);
    const winningTrades = sellTrades.filter(trade => (trade.profit_amount || 0) > 0).length;
    const winRate = (winningTrades / totalTrades) * 100;
    const avgProfit = totalProfitAmount / totalTrades;
    const maxProfit = Math.max(...sellTrades.map(t => t.profit_amount || 0));
    const minProfit = Math.min(...sellTrades.map(t => t.profit_amount || 0));
    
    return {
      totalTrades,
      totalProfit: capital > 0 ? (totalProfitAmount / capital) * 100 : 0,
      totalProfitAmount,
      winRate,
      avgProfit,
      maxProfit,
      minProfit,
      totalFees
    };
  };

  if (!bot) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Bot Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">The bot you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/my-bots')}>
              Back to My Bots
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <main className="p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/my-bots')}
                    className="flex items-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to My Bots</span>
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {bot.title || bot.name || 'Bot Statistics'}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                      {getStrategyLabel(bot.strategy, bot.strategy_key)} • {bot.exchange || 'Unknown Exchange'}
                    </p>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Profit</p>
                          <p className={cn(
                            "text-2xl font-bold",
                            (botStats?.totalProfit || 0) >= 0 ? "text-green-500" : "text-red-500"
                          )}>
                            {formatPct(botStats?.totalProfit)}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-green-500" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Trades</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatNumber(botStats?.totalTrades, 0)}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                          <BarChart3 className="h-6 w-6 text-blue-500" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Win Rate</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatNumber(botStats?.winRate, 1)}%
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                          <Target className="h-6 w-6 text-purple-500" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Profit</p>
                          <p className={cn(
                            "text-2xl font-bold",
                            (botStats?.avgProfit || 0) >= 0 ? "text-green-500" : "text-red-500"
                          )}>
                            {formatCurrency(botStats?.avgProfit, bot.quote_currency || 'USDT')}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center">
                          <Activity className="h-6 w-6 text-yellow-500" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Trades */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Recent Trades</h2>
                    </div>
                    
                    {trades.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Pair
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Price
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Profit
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {trades.map((trade) => (
                              <tr key={trade.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {new Date(trade.timestamp).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  {trade.pair}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-xs font-medium",
                                    trade.type === 'buy' 
                                      ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                                      : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                                  )}>
                                    {trade.type.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {formatNumber(trade.amount, 6)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {formatCurrency(trade.price, trade.quote_currency)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {trade.profit_amount !== null ? (
                                    <span className={cn(
                                      "font-medium",
                                      (trade.profit_amount || 0) >= 0 ? "text-green-600" : "text-red-600"
                                    )}>
                                      {formatCurrency(trade.profit_amount, trade.quote_currency)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Trades Yet</h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          This bot hasn't executed any trades yet. Activate it to start trading.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
        </motion.div>
      </main>
    </DashboardLayout>
  );
};

export default BotStatsPage;